import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/auth/AuthContext';
import { isWithinFacilityRange, formatDistanceFeet } from '../../utils/geolocation';

interface TimeLog {
  id: string;
  clock_in_time: string;
  clock_out_time: string | null;
  status: string;
  hours_worked?: number;
}

const TimeClock: React.FC = () => {
  const { userProfile } = useAuth();
  const [currentLog, setCurrentLog] = useState<TimeLog | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const [checkingLocation, setCheckingLocation] = useState(false);

  useEffect(() => {
    fetchCurrentLog();
  }, [userProfile]);

  const fetchCurrentLog = async () => {
    if (!userProfile?.id) return;

    try {
      // Get staff record
      const { data: staffData } = await supabase
        .from('team')
        .select('id')
        .eq('user_id', userProfile.id)
        .single();

      if (!staffData) return;

      // Check for active clock-in
      const { data, error } = await supabase
        .from('crew_time_logs')
        .select('*')
        .eq('staff_id', staffData.id)
        .eq('status', 'clocked_in')
        .order('clock_in_time', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching current log:', error);
        return;
      }

      setCurrentLog(data);
    } catch (err) {
      console.error('Error in fetchCurrentLog:', err);
    }
  };

  const checkLocation = async (): Promise<{ latitude: number; longitude: number } | null> => {
    setCheckingLocation(true);
    setError(null);
    setDistance(null);

    try {
      const result = await isWithinFacilityRange();
      setDistance(result.distance);
      return result.userLocation;
    } catch (err: any) {
      // Location errors are now non-blocking - just log and return null
      console.warn('Location unavailable:', err.message || err);
      return null;
    } finally {
      setCheckingLocation(false);
    }
  };

  const handleClockIn = async () => {
    if (!userProfile?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      // Get location (optional - won't block if unavailable)
      const location = await checkLocation();

      // Get staff record
      const { data: staffData, error: staffError } = await supabase
        .from('team')
        .select('id')
        .eq('user_id', userProfile.id)
        .single();

      if (staffError || !staffData) {
        setError('Unable to find your staff record');
        setIsLoading(false);
        return;
      }

      // Create clock-in record
      const { data, error: insertError } = await supabase
        .from('crew_time_logs')
        .insert({
          staff_id: staffData.id,
          clock_in_time: new Date().toISOString(),
          clock_in_latitude: location?.latitude ?? null,
          clock_in_longitude: location?.longitude ?? null,
          status: 'clocked_in',
        })
        .select()
        .single();

      if (insertError) {
        setError('Failed to clock in: ' + insertError.message);
        setIsLoading(false);
        return;
      }

      setCurrentLog(data);
      setError(null);
    } catch (err: any) {
      setError('An error occurred: ' + (err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!currentLog) return;

    setIsLoading(true);
    setError(null);

    try {
      // Get location (optional - won't block if unavailable)
      const location = await checkLocation();

      // Calculate shift duration to detect forgotten clock-outs
      const clockInTime = new Date(currentLog.clock_in_time);
      const now = new Date();
      const hoursWorked = (now.getTime() - clockInTime.getTime()) / (1000 * 60 * 60);

      // Max shift duration before considering it a "forgotten" clock-out
      const MAX_SHIFT_HOURS = 12;
      const DEFAULT_SHIFT_HOURS = 1;

      let clockOutTime: Date;
      let wasAutoCapped = false;

      if (hoursWorked > MAX_SHIFT_HOURS) {
        // Shift is too long - they probably forgot to clock out
        // Cap it to 1 hour from clock-in time
        clockOutTime = new Date(clockInTime.getTime() + DEFAULT_SHIFT_HOURS * 60 * 60 * 1000);
        wasAutoCapped = true;
      } else {
        clockOutTime = now;
      }

      // Update clock-out record
      const { error: updateError } = await supabase
        .from('crew_time_logs')
        .update({
          clock_out_time: clockOutTime.toISOString(),
          clock_out_latitude: location?.latitude ?? null,
          clock_out_longitude: location?.longitude ?? null,
          status: 'clocked_out',
          notes: wasAutoCapped
            ? `Auto-adjusted: Original duration was ${hoursWorked.toFixed(1)}h (exceeded ${MAX_SHIFT_HOURS}h max). Set to ${DEFAULT_SHIFT_HOURS}h.`
            : null,
        })
        .eq('id', currentLog.id);

      if (updateError) {
        setError('Failed to clock out: ' + updateError.message);
        setIsLoading(false);
        return;
      }

      setCurrentLog(null);

      // Show warning if shift was auto-capped
      if (wasAutoCapped) {
        setError(`Note: Your shift was automatically adjusted to ${DEFAULT_SHIFT_HOURS} hour because you forgot to clock out (was ${hoursWorked.toFixed(1)} hours). Please contact admin if this needs correction.`);
      } else {
        setError(null);
      }
    } catch (err: any) {
      setError('An error occurred: ' + (err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Chicago',
    });
  };

  const calculateHoursWorked = () => {
    if (!currentLog) return '0:00';
    const start = new Date(currentLog.clock_in_time).getTime();
    const end = Date.now();
    const diff = end - start;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">Time Clock</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {distance !== null && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-700 text-sm">
          Your location: {formatDistanceFeet(distance)} from facility
        </div>
      )}

      {currentLog ? (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-green-800">Currently Clocked In</span>
              <span className="inline-block w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
            </div>
            <p className="text-2xl font-semibold text-green-900">
              {formatTime(currentLog.clock_in_time)}
            </p>
            <p className="text-sm text-green-700 mt-2">
              Time worked: <span className="font-medium">{calculateHoursWorked()}</span>
            </p>
          </div>

          <button
            onClick={handleClockOut}
            disabled={isLoading || checkingLocation}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              isLoading || checkingLocation
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            {checkingLocation ? 'Checking Location...' : isLoading ? 'Clocking Out...' : 'Clock Out'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-gray-600">You are not clocked in</p>
          </div>

          <button
            onClick={handleClockIn}
            disabled={isLoading || checkingLocation}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              isLoading || checkingLocation
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-[#3A5E63] hover:bg-[#2d4a4e] text-white'
            }`}
          >
            {checkingLocation ? 'Checking Location...' : isLoading ? 'Clocking In...' : 'Clock In'}
          </button>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500 text-center">
        Your location will be recorded when you clock in/out
      </div>
    </div>
  );
};

export default TimeClock;

















